// Auth intent contract (CREATE-LEAGUE-FUNNEL.md §3): a fixed, allowlisted
// funnel marker carried in the URL while the user is on /signup or /login.
//
// The URL is the one medium that survives the storage discontinuities that
// eat localStorage/sessionStorage state on iOS (private tabs, in-app-browser
// hops, tab jettison) - see the 2026-08-10 incident in the scope doc. The
// intent never carries a redirect target or payload data; it only names
// which funnel the user came from, and completeLogin maps it to a hardcoded
// destination. Unknown values are ignored, never echoed.
//
// Depends on web Apple/Google auth being popup-based (no full-page redirect
// while on the auth pages). If a provider is ever switched to redirect mode,
// this contract must move into the OAuth `state` parameter.

export const AUTH_INTENT_PARAM = 'intent';

const ALLOWED_INTENTS = ['create_league'] as const;

export type AuthIntent = (typeof ALLOWED_INTENTS)[number];

// Where completeLogin sends a create_league login. The wizard owns
// resume-vs-recovery from there (CreateLeaguePage's ?resume handling).
export const CREATE_LEAGUE_RESUME_PATH = '/season-long/create-league?resume=league_creation';

export const readAuthIntent = (search: string): AuthIntent | null => {
  const raw = new URLSearchParams(search).get(AUTH_INTENT_PARAM);
  return raw !== null && (ALLOWED_INTENTS as readonly string[]).includes(raw)
    ? (raw as AuthIntent)
    : null;
};

// Appends the intent to a same-app path (signup/login toggles). No-op when
// there is no intent, so ordinary auth links stay clean.
export const withAuthIntent = (path: string, intent: AuthIntent | null): string =>
  intent ? `${path}${path.includes('?') ? '&' : '?'}${AUTH_INTENT_PARAM}=${intent}` : path;
